import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Field } from '@vnetwork/ui/forms';
import {
  Button,
  Input,
  Textarea,
  Checkbox,
  IconButton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Skeleton,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import {
  useServiceCustomRule,
  useCreateServiceCustomRule,
  useUpdateServiceCustomRule,
  useServiceAdvancedEnabledSites,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './CustomRuleForm.module.css';

const NAME_MAX = 26;
const DESC_MAX = 200;

/** Metrics (sqlinjection_item.key). "Request Header Value" is special: the vendor
 *  key is `Request_Headers:<HeaderName>` and the item also carries `value_key`, so
 *  it is selected here via a sentinel and the header name is entered separately. */
const HEADER_VALUE_METRIC = 'Request_Headers';
const METRICS = [
  { value: 'Request_URL', label: 'Path' },
  { value: 'Request_Method', label: 'HTTP Method' },
  { value: 'Request_Protocol', label: 'Protocol' },
  { value: 'Request_Parameter_Name', label: 'Request Parameter Name' },
  { value: 'Request_Header_Name', label: 'Request Header Name' },
  { value: HEADER_VALUE_METRIC, label: 'Request Header Value' },
  { value: 'Request_Referer', label: 'Referer' },
] as const;
/** Matching conditions (sqlinjection_item.operation). Only Regex is confirmed. */
const MATCH_CONDITIONS = [{ value: 'regex', label: 'Regex' }] as const;
/** Action policies. Only "Block" is confirmed; Monitor / By-pass values are
 *  best-effort and may need adjusting against the portal. */
const ACTIONS = [
  { value: 'Block', label: 'Block' },
  { value: 'Monitor', label: 'Monitor' },
  { value: 'Bypass', label: 'By-pass' },
] as const;

const labelOf = (cat: readonly { value: string; label: string }[], v: string) =>
  cat.find((o) => o.value === v)?.label ?? v;

/** One triggering condition, serialized verbatim into `sqlinjection_item`. For a
 *  "Request Header Value" condition `key` is `Request_Headers:<name>` and
 *  `value_key` holds `<name>`; other metrics omit `value_key`. */
interface RuleItem {
  key: string;
  operation: string;
  value: string;
  value_key?: string;
}

/** The triggering-condition draft row. `metric` is the METRICS selector value
 *  (a sentinel for header-value); `headerName` is only used for that metric. */
interface DraftItem {
  metric: string;
  operation: string;
  headerName: string;
  value: string;
}

/** Whether a stored item is a "Request Header Value" condition. */
const isHeaderValueItem = (r: RuleItem) =>
  !!r.value_key || r.key.startsWith(`${HEADER_VALUE_METRIC}:`);

/** Display label for a stored item's Metrics cell. */
const metricLabel = (r: RuleItem) => {
  if (isHeaderValueItem(r)) {
    const name = r.value_key || r.key.split(':')[1] || '';
    return name ? `Request Header Value (${name})` : 'Request Header Value';
  }
  return labelOf(METRICS, r.key);
};

/** Convert an editable draft into a stored item. */
const buildItem = (d: DraftItem): RuleItem => {
  const value = d.value.trim();
  if (d.metric === HEADER_VALUE_METRIC) {
    const name = d.headerName.trim();
    return { key: `${HEADER_VALUE_METRIC}:${name}`, operation: d.operation, value, value_key: name };
  }
  return { key: d.metric, operation: d.operation, value };
};

/** Convert a stored item back into an editable draft. */
const toDraft = (r: RuleItem): DraftItem =>
  isHeaderValueItem(r)
    ? {
        metric: HEADER_VALUE_METRIC,
        operation: r.operation,
        headerName: r.value_key || r.key.split(':')[1] || '',
        value: r.value,
      }
    : { metric: r.key, operation: r.operation, headerName: '', value: r.value };

/** The Custom Rule config (the FE owns the type-specific fields; the BE injects
 *  name / sqlinjection_comment / sites). */
type CustomRuleConfig = Record<string, unknown>;

/** Fixed fields the portal sends but the form doesn't expose. */
const HIDDEN_CONFIG = {
  sqlinjection_item_v: '',
  sqlinjection_detect_type: 'USER-DEFINE',
  sqlinjection_detect_direction: 'request',
  hosts: [] as string[],
  risk_level: 'high',
  decode_enable: false,
  decode_sequence: ['url_decode'],
};

interface FormState {
  name: string;
  description: string;
  sites: number[];
  items: RuleItem[];
  action: string;
  block_value: number;
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  sites: [],
  items: [],
  action: 'Block',
  block_value: 400,
});

const emptyDraft: DraftItem = {
  metric: METRICS[0].value,
  operation: MATCH_CONDITIONS[0].value,
  headerName: '',
  value: '',
};

/**
 * Add / Edit a Custom Rule (AI-WAF → Custom Rules, custom_strategy surface).
 * Builds the per-rule `config` (sqlinjection_item conditions + action + the fixed
 * detection fields); the BE injects name / sqlinjection_comment / sites. Routes:
 * aiwaf/custom-rules/new + /:id.
 */
export function CustomRuleForm() {
  const navigate = useNavigate();
  const { serviceId, id } = useParams<{ serviceId: string; id: string }>();
  const isEdit = !!id;
  const { show } = useToast();
  const base = `/services/waap/${serviceId}`;

  const { data: existing, isLoading: loadingExisting } = useServiceCustomRule<CustomRuleConfig>(serviceId, id);
  const { data: sites = [] } = useServiceAdvancedEnabledSites(serviceId);

  const [form, setForm] = useState<FormState>(emptyForm);
  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!existing) return;
    const c = (existing.config ?? {}) as CustomRuleConfig;
    setForm({
      name: existing.name ?? '',
      description: existing.description ?? '',
      sites: existing.sites ?? [],
      items: Array.isArray(c.sqlinjection_item) ? (c.sqlinjection_item as RuleItem[]) : [],
      action: (c.action as string) || 'Block',
      block_value: typeof c.block_value === 'number' ? c.block_value : 400,
    });
  }, [existing]);

  // Triggering-condition draft row.
  const [draft, setDraft] = useState<DraftItem>(emptyDraft);
  const isHeaderValueDraft = draft.metric === HEADER_VALUE_METRIC;
  const addItem = () => {
    if (!draft.value.trim()) return;
    if (isHeaderValueDraft && !draft.headerName.trim()) return;
    set('items', [...form.items, buildItem(draft)]);
    setDraft(emptyDraft);
  };
  const removeItem = (i: number) => set('items', form.items.filter((_, idx) => idx !== i));
  const editItem = (i: number) => {
    setDraft(toDraft(form.items[i]));
    removeItem(i);
  };

  const siteSet = useMemo(() => new Set(form.sites), [form.sites]);
  const toggleSite = (sid: number) =>
    set('sites', siteSet.has(sid) ? form.sites.filter((x) => x !== sid) : [...form.sites, sid]);

  const create = useCreateServiceCustomRule<CustomRuleConfig>(serviceId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'custom_rule', 'create');
      show({ tone: 'success', title: 'Custom rule created' });
      navigate(`${base}/aiwaf/custom-rules`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Create failed', description: errMessage(e) }),
  });
  const update = useUpdateServiceCustomRule<CustomRuleConfig>(serviceId ?? '', id ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'custom_rule', 'edit');
      show({ tone: 'success', title: 'Custom rule saved' });
      navigate(`${base}/aiwaf/custom-rules`);
    },
    onError: (e) => show({ tone: 'danger', title: 'Save failed', description: errMessage(e) }),
  });
  const saving = create.isPending || update.isPending;

  const nameValid = form.name.trim().length > 0 && form.name.trim().length <= NAME_MAX;
  const actionValid = form.action !== 'Block' ? true : form.block_value > 0;
  const canSubmit = nameValid && form.items.length > 0 && actionValid && !saving;

  const submit = () => {
    const config: CustomRuleConfig = {
      ...HIDDEN_CONFIG,
      sqlinjection_item: form.items,
      action: form.action,
      block_value: form.block_value,
    };
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      sites: form.sites,
      config,
    };
    if (isEdit) update.mutate(payload);
    else create.mutate(payload);
  };

  if (isEdit && loadingExisting) {
    return (
      <Container size="xl">
        <Skeleton style={{ height: 320 }} />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={
          <WaapConsoleBreadcrumbs
            trail={[
              { label: 'AI-WAF' },
              { label: 'Custom Rules', slug: 'aiwaf/custom-rules' },
              { label: isEdit ? 'Edit Custom Rule' : 'Custom Rule' },
            ]}
          />
        }
        title="Custom Rule"
      />

      <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
        <Card flat>
          <CardHeader>
            <CardTitle>General</CardTitle>
          </CardHeader>
          <CardBody>
            <div className={styles.formGrid}>
              <Field
                label="Name"
                required
                description={`1-${NAME_MAX} characters. Valid: 0-9, A-Z, a-z, space, . _ - ( ) * +`}
              >
                {(props) => (
                  <Input
                    {...props}
                    value={form.name}
                    maxLength={NAME_MAX}
                    placeholder="Rule name"
                    onChange={(e) => set('name', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Description" description={`Up to ${DESC_MAX} characters.`}>
                {(props) => (
                  <div>
                    <Textarea
                      {...props}
                      value={form.description}
                      maxLength={DESC_MAX}
                      placeholder="Description"
                      onChange={(e) => set('description', e.target.value)}
                    />
                    <div className={styles.charCount}>
                      {form.description.length}/{DESC_MAX}
                    </div>
                  </div>
                )}
              </Field>
            </div>
          </CardBody>
        </Card>

        {/* Triggering Condition: contains a rule-builder table — kept as Card wrapping the full section */}
        <Card flat>
          <CardHeader>
            <CardTitle>Triggering Condition</CardTitle>
            <CardDescription>A request matches the rule when it satisfies the conditions below.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.condRow}>
              <Field label="Metrics" required>
                {() => (
                  <Select
                    value={draft.metric}
                    onValueChange={(v) => setDraft((d) => ({ ...d, metric: v }))}
                  >
                    <SelectTrigger className={styles.metricSelect}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {METRICS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Matching Condition" required>
                {() => (
                  <Select
                    value={draft.operation}
                    onValueChange={(v) => setDraft((d) => ({ ...d, operation: v }))}
                  >
                    <SelectTrigger className={styles.matchSelect}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MATCH_CONDITIONS.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {isHeaderValueDraft && (
                <Field label="Header Name" required>
                  {(props) => (
                    <Input
                      {...props}
                      value={draft.headerName}
                      placeholder="e.g. X-Header"
                      className={styles.headerInput}
                      onChange={(e) => setDraft((d) => ({ ...d, headerName: e.target.value }))}
                    />
                  )}
                </Field>
              )}
              <Field label="Value" required>
                {(props) => (
                  <Input
                    {...props}
                    value={draft.value}
                    placeholder="Regular expression used for detection"
                    className={styles.valueInput}
                    onChange={(e) => setDraft((d) => ({ ...d, value: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addItem();
                      }
                    }}
                  />
                )}
              </Field>
              <Button variant="secondary" startIcon={<Plus size={14} />} onClick={addItem}>
                Add
              </Button>
            </div>

            <RuleItemTable rows={form.items} onEdit={editItem} onRemove={removeItem} />
          </CardBody>
        </Card>

        <Card flat>
          <CardHeader>
            <CardTitle>Action</CardTitle>
            <CardDescription>Configure how requests that satisfy the triggering condition are handled.</CardDescription>
          </CardHeader>
          <CardBody>
            <div className={styles.actionGrid}>
              <Field label="Action" required>
                {() => (
                  <Select value={form.action} onValueChange={(v) => set('action', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map((a) => (
                        <SelectItem key={a.value} value={a.value}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {form.action === 'Block' && (
                <Field label="Response Status Code" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="number"
                      min={100}
                      max={599}
                      value={String(form.block_value)}
                      className={styles.statusInput}
                      onChange={(e) => set('block_value', Number(e.target.value))}
                    />
                  )}
                </Field>
              )}
            </div>
          </CardBody>
        </Card>

        <Card flat>
          <CardHeader>
            <CardTitle>Apply to</CardTitle>
            <CardDescription>Select the websites this rule protects.</CardDescription>
          </CardHeader>
          <CardBody>
            {sites.length === 0 ? (
              <div className={styles.muted}>
                No protection-enabled websites available.
              </div>
            ) : (
              <div className={styles.siteList}>
                {sites.map((s) => (
                  <label key={s.id} className={styles.checkRow}>
                    <Checkbox checked={siteSet.has(s.id)} onCheckedChange={() => toggleSite(s.id)} />
                    <span>{s.domain}</span>
                  </label>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <div className={styles.formActions}>
          <Button variant="secondary" onClick={() => navigate(`${base}/aiwaf/custom-rules`)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={submit}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </Container>
  );
}

/** Triggering-condition table — Metrics / Matching Condition / Value + Edit/Delete. */
function RuleItemTable({
  rows,
  onEdit,
  onRemove,
}: {
  rows: RuleItem[];
  onEdit: (i: number) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className={styles.table}>
      <div className={styles.tableHead}>
        <div className={styles.cell}>Metrics</div>
        <div className={styles.cell}>Matching Condition</div>
        <div className={styles.cell}>Value</div>
        <div className={styles.cell}>Operation</div>
      </div>
      {rows.length === 0 ? (
        <div className={`${styles.cell} ${styles.cellEmpty}`}>
          No data
        </div>
      ) : (
        rows.map((r, i) => (
          <div key={i} className={styles.tableRow}>
            <div className={styles.cell}>{metricLabel(r)}</div>
            <div className={styles.cell}>{labelOf(MATCH_CONDITIONS, r.operation)}</div>
            <div className={styles.cell}>{r.value}</div>
            <div className={`${styles.cell} ${styles.cellActions}`}>
              <IconButton aria-label="Edit" variant="ghost" onClick={() => onEdit(i)}>
                <Pencil size={16} />
              </IconButton>
              <IconButton aria-label="Remove" variant="ghost" onClick={() => onRemove(i)}>
                <Trash2 size={16} />
              </IconButton>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
